// Reglas puras de `enterprise` (plan.md §2, TS puro — sin React/axios/MUI, constitution.md #2).
// Extraídas literal de `EnterpriseAdminPage.jsx` (spec.md §5).

import type {
  Branch,
  BranchFormValues,
  Enterprise,
  EnterpriseGeneralFormValues,
  EnterpriseTab,
  EnterpriseTabId,
  ValidationResult,
} from './enterprise.types';
import type { OrganizationalUnitFormValues } from './organizationalUnit.types';

// UC-1 (`EnterpriseAdminPage.jsx:121-142`): single-tenant por convención — toma `data[0]` con
// guard de lista no vacía.
export function selectOwnerEnterprise(list: Enterprise[]): Enterprise | null {
  return list.length > 0 ? list[0] : null;
}

// UC-2 (`:126-135`): precarga del form con fallback `|| ''` por campo.
export function toEnterpriseFormValues(ent: Enterprise): EnterpriseGeneralFormValues {
  return {
    name: ent.name || '',
    nit: ent.nit || '',
    address: ent.address || '',
    city: ent.city || '',
    state: ent.state || '',
    country_id: ent.country_id || '',
    org_type: ent.org_type || '',
    logo_url: ent.logo_url || '',
  };
}

// UC-3 (`:217-222`): reemplaza en `index` si `>= 0`, si no hace `push`. No muta el array recibido.
export function upsertBranch(branches: Branch[], branch: Branch, index: number): Branch[] {
  const updated = [...branches];
  if (index >= 0) {
    updated[index] = branch;
  } else {
    updated.push(branch);
  }
  return updated;
}

// UC-3 (`:241`): filtra por índice, preserva el resto.
export function removeBranchAt(branches: Branch[], index: number): Branch[] {
  return branches.filter((_, i) => i !== index);
}

// UC-3 (`:212-216`): `name` y `city` son requeridos.
export function validateBranch(data: BranchFormValues): ValidationResult<'nameAndCityRequired'> {
  if (!data.name.trim() || !data.city.trim()) {
    return { ok: false, error: 'nameAndCityRequired' };
  }
  return { ok: true };
}

// UC-4 (`:256-260`/`:302-306`): `code` y `name` son requeridos (DU y PU, decisión J).
export function validateOrganizationalUnit(
  data: OrganizationalUnitFormValues
): ValidationResult<'codeAndNameRequired'> {
  if (!data.code.trim() || !data.name.trim()) {
    return { ok: false, error: 'codeAndNameRequired' };
  }
  return { ok: true };
}

// UC-9/UC-12 (`:66-76`): tabs dinámicos por permiso. `general`+`branches` siempre; `du`/`pu`/
// `branding` solo si `canManage`.
export function buildEnterpriseTabs(canManage: boolean): EnterpriseTab[] {
  return [
    { id: 'general', labelKey: 'enterprise.tabGeneral' },
    { id: 'branches', labelKey: 'enterprise.tabBranches' },
    ...(canManage
      ? ([
          { id: 'du', labelKey: 'enterprise.tabDU' },
          { id: 'pu', labelKey: 'enterprise.tabPU' },
          { id: 'branding', labelKey: 'enterprise.tabBranding' },
        ] as EnterpriseTab[])
      : []),
  ];
}

// UC-12 (`:77`): `tabs[tab]?.id || 'general'` — índice fuera de rango cae a 'general'.
export function currentTabId(tabs: EnterpriseTab[], index: number): EnterpriseTabId {
  return tabs[index]?.id || 'general';
}
